import React, { useEffect, useMemo, useState } from "react";
import useMediaQuery from "@mui/material/useMediaQuery";
import { MdChevronLeft, MdChevronRight } from "react-icons/md";
import { GdanskSaleForm } from "../../../models/GdanskSaleForm";
import { get } from "../../../api";
import { GET_LEGACY_EXERCISES } from "../../../api/apiRoutes";
import useFacility from "../../../../utils/hooks/useFacility";
import { GDANSK_PASS } from "../Steps/gdanskSaleStepsConsts";
import { format } from "date-fns";
import EASkeletonLoader from "../../shared/EASkeletonLoader";
import { GdanskLaneExercises } from "../../../models/GdanskLaneExercises";
import { ExerciseInt } from "../../../models/ExerciseInt";
import GdanskLaneExerciseTile from "./GdanskLaneExerciseTile";
import getId from "../../../../utils/getId";
import EAIconButton from "../../shared/Buttons/EAIconButton";

interface IProps {
  form: GdanskSaleForm;
  setForm: (form: GdanskSaleForm) => void;
  isInstructor?: boolean;
}

const GdanskLaneSlotLegend: React.FC = () => (
  <div className="gdansk-lane-slot-legend" role="region" aria-label="Legenda dostępności slotów">
    <div className="gdansk-lane-slot-legend__item">
      <span className="gdansk-lane-slot-legend__swatch gdansk-lane-slot-legend__swatch--available" aria-hidden />
      <p className="no-line-height">Miejsca dostępne w sprzedaży internetowej.</p>
    </div>
    <div className="gdansk-lane-slot-legend__item">
      <span className="gdansk-lane-slot-legend__swatch gdansk-lane-slot-legend__swatch--full" aria-hidden />
      <p className="no-line-height">
        Pula biletów przeznaczonych do sprzedaży internetowej została wyczerpana. W celu sprawdzenia dostępności miejsc
        w kasie pływalni zapraszamy do kontaktu telefonicznego.
      </p>
    </div>
  </div>
);

const GdanskLaneSelectInput: React.FC<IProps> = ({ form, setForm, isInstructor = false }) => {
  const facilityIds = useFacility();
  const isMobile = useMediaQuery("(max-width:980px)");
  const [loading, setLoading] = useState(false);
  const [exercises, setExercises] = useState<GdanskLaneExercises>({} as GdanskLaneExercises);
  const [mobileLaneIndex, setMobileLaneIndex] = useState(0);

  const getExercises = async () => {
    setLoading(true);
    const dateFromAndDateTo = format(form.date, "yyyy-MM-dd");
    const exerciseGroupIds = isInstructor ? "1,114" : ""; // DODATKOWY PARAMETR SZYTWNIAK, ABY ZWRACAĆ TOR DLA INTRUKTORA
    const { result, isError } = await get(
      GET_LEGACY_EXERCISES(
        facilityIds,
        form.productMainType === GDANSK_PASS,
        dateFromAndDateTo,
        dateFromAndDateTo,
        exerciseGroupIds,
      ),
    );
    if (!isError) {
      // Sortowanie kluczy (godzin) w obiekcie result
      const sortedResult = Object.keys(result)
        .sort((timeA, timeB) => {
          const [startA] = timeA.split("-");
          const [startB] = timeB.split("-");
          return new Date(`1970-01-01T${startA}:00`).getTime() - new Date(`1970-01-01T${startB}:00`).getTime();
        })
        .reduce(
          (acc, key) => {
            acc[key] = result[key]; // Tworzymy posortowany obiekt
            return acc;
          },
          {} as typeof result,
        );

      setExercises(sortedResult);
    }
    setLoading(false);
  };

  useEffect(() => {
    if (form.date) {
      getExercises();
    }
    //eslint-disable-next-line
  }, [form.date, form.productMainType]);

  const timeSlotKeys = useMemo(() => Object.keys(exercises), [exercises]);

  const columnSlots = useMemo(() => {
    if (timeSlotKeys.length === 0) {
      return [] as string[];
    }
    return Object.keys(exercises[timeSlotKeys[0]]);
  }, [exercises, timeSlotKeys]);

  useEffect(() => {
    setMobileLaneIndex(0);
  }, [exercises]);

  useEffect(() => {
    if (mobileLaneIndex > 0 && mobileLaneIndex >= columnSlots.length) {
      setMobileLaneIndex(Math.max(0, columnSlots.length - 1));
    }
  }, [columnSlots.length, mobileLaneIndex]);

  if (!form.date) {
    return null;
  }

  if (loading) {
    return <EASkeletonLoader height="300px" />;
  }

  if (Object.keys(exercises).length === 0) {
    return (
      <div className="alert alert-yellow">
        <p>Niestety, nie znaleźliśmy żadnych terminów dla podanego dnia.</p>
      </div>
    );
  }

  if (columnSlots.length === 0) {
    return (
      <div className="alert alert-yellow">
        <p>Niestety, nie znaleźliśmy żadnych torów dla podanego dnia.</p>
      </div>
    );
  }

  const currentLaneKey = columnSlots[mobileLaneIndex];
  const canGoPrev = mobileLaneIndex > 0;
  const canGoNext = mobileLaneIndex < columnSlots.length - 1;

  const renderMobileLanePicker = () => (
    <>
      <div className="gdansk-lane-select__mobile-header">
        <EAIconButton
          title="Poprzedni tor"
          variant="icon"
          icon={<MdChevronLeft />}
          onClick={() => setMobileLaneIndex((i) => Math.max(0, i - 1))}
          disabled={!canGoPrev}
        />
        <span className="gdansk-lane-select__mobile-lane-name">{currentLaneKey}</span>
        <EAIconButton
          title="Następny tor"
          variant="icon"
          icon={<MdChevronRight />}
          onClick={() => setMobileLaneIndex((i) => Math.min(columnSlots.length - 1, i + 1))}
          disabled={!canGoNext}
        />
      </div>
      <div className="gdansk-lane-select__mobile-rows">
        {timeSlotKeys.map((timeSlot) => {
          const laneExercises: ExerciseInt[] = exercises[timeSlot][currentLaneKey] ?? [];

          return (
            <div key={timeSlot} className="gdansk-lane-select__mobile-row">
              <div className="gdansk-lane-select__mobile-time">{timeSlot}</div>
              <div className="gdansk-lane-select__mobile-tile">
                {laneExercises.map((exercise) => (
                  <GdanskLaneExerciseTile
                    key={exercise.objectId}
                    exercise={exercise}
                    onSelect={(value) => setForm({ ...form, exercise: value })}
                    isSelected={Boolean(getId(form.exercise) && getId(form.exercise) === getId(exercise))}
                  />
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </>
  );

  return (
    <div className="gdansk-lane-select">
      {isMobile ? (
        <>
          {renderMobileLanePicker()}
          <GdanskLaneSlotLegend />
        </>
      ) : (
        <>
          <table className="lane-table">
            <thead>
              <tr>
                <th>Godzina</th>
                {Object.keys(exercises).length > 0 &&
                  Object.keys(exercises[Object.keys(exercises)[0]]).map((columnSlot) => (
                    <th key={columnSlot}>{columnSlot}</th>
                  ))}
              </tr>
            </thead>
            <tbody>
              {Object.keys(exercises).map((timeSlot) => (
                <tr key={timeSlot}>
                  <td>{timeSlot}</td>
                  {Object.keys(exercises[timeSlot]).map((columnSlot) => {
                    const exercisesInSlot: ExerciseInt[] = exercises[timeSlot][columnSlot];

                    return (
                      <td key={columnSlot}>
                        {exercisesInSlot.map((exercise) => (
                          <GdanskLaneExerciseTile
                            key={exercise.objectId}
                            exercise={exercise}
                            onSelect={(value) => setForm({ ...form, exercise: value })}
                            isSelected={Boolean(getId(form.exercise) && getId(form.exercise) === getId(exercise))}
                          />
                        ))}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
          <GdanskLaneSlotLegend />
        </>
      )}
    </div>
  );
};

export default GdanskLaneSelectInput;
